/* Parts shared by the administration screens (Administration, People, Organisation, Financials):
   the fact row, the tab area, and the two disclosures that keep a long explanation or a creating
   form out of the way until it is asked for. Tokens only (ADR-0007). */

/* The tab area is one grid item spanning the shell's body, so a tab's contents stack in it rather
   than being placed on the twelve columns one by one (Inventory.module.css does the same). */
.area {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

/* A panel is a column flex box with `min-height: 0`; in a stack with no fixed height it must keep
   its content height, or a long panel would be squeezed against its neighbours. */
.stack > * {
  flex: none;
}

/* Two panels side by side on the shell's twelve columns (each brings its own `column`), one under
   the other when the screen is narrow. Both sit in the same row, so the taller sets its height and
   neither is clipped. `section` rather than a class: a panel is a `section` (Panel.tsx), and this
   must outrank the panel's own `grid-column: var(--panel-col)`. */
.twoCol {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--gap);
  align-items: start;
  min-width: 0;
}

@media (width <= 900px) {
  .twoCol {
    grid-template-columns: minmax(0, 1fr);
  }

  .twoCol > section {
    grid-column: 1;
    grid-row: auto;
  }
}

/* The facts that answer the screen's question. Four across; two at tablet width; one on a phone.
   `minmax(0, 1fr)` so a long note cannot push a tile past the viewport. */
.facts {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

.facts3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (width <= 1100px) {
  .facts,
  .facts3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (width <= 560px) {
  .facts,
  .facts3 {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ------------------------------------------------------------------------ the disclosures */

.disclosure,
.add,
.basis {
  min-width: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
  background: var(--panel);
}

.disclosureSummary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
  list-style: none;
}

.disclosureSummary::-webkit-details-marker {
  display: none;
}

.disclosureSummary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.disclosureSummary:hover {
  color: var(--accent);
}

/* The caret turns when the disclosure opens; the words carry the meaning, the glyph is chrome. */
.caret {
  flex: none;
  transition: transform var(--dur-1) var(--ease-std);
}

details[open] > .disclosureSummary .caret {
  transform: rotate(90deg);
}

@media (prefers-reduced-motion: reduce) {
  .caret,
  .basisSummary::before {
    transition: none;
  }
}

/* "Basis and limits" draws its caret in CSS, not as an icon element: it is mounted inside panels
   whose tests assert the exact set of elements and attributes they render (the licence counts
   carry no attribute but their declared ones), and an SVG would add seven. */
.basisSummary::before {
  content: "";
  flex: none;
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 6px solid currentcolor;
  transition: transform var(--dur-1) var(--ease-std);
}

.basis[open] > .basisSummary::before {
  transform: rotate(90deg);
}

.disclosureBody {
  padding: 0 14px 12px;
  min-width: 0;
  overflow-wrap: anywhere;
}

/* A creating form is a panel opened by a button, not a block that sits under the list for ever:
   the list is what the screen is for, and the form is what you reach for once. */
.add {
  margin-top: 14px;
}

.add > .disclosureSummary {
  color: var(--accent);
}

.add .disclosureBody {
  padding-top: 0;
}

/* The form inside brings its own border and margin (Organisation.module.css `.form`); inside a
   panel that would draw a box in a box. */
.add .disclosureBody > form {
  margin-top: 0;
  border: 0;
  padding: 0 0 4px;
  background: none;
}

.basis .disclosureBody > p {
  margin: 8px 0 0;
  font-size: var(--t-min);
  line-height: 1.55;
  color: var(--ink-2);
}

.basis .disclosureBody > p:first-child {
  margin-top: 0;
}

/* A tab's tablist sits under the header with its own rule; nothing here restyles it. */
